import { Observable, Subject, catchError, forkJoin, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { IParkerExecutionStatus, ParkerInfo } from "../parker-execution-status";
import { ProgressInfo } from "../progress-info";
import { PagedResultDTOOfParkerDTO, ParkerDTO } from 'src/app/base/models/models.swagger.generated';

export abstract class ParkerFunctionExecutor {
  public functionExecuted: Subject<void> = new Subject<void>();
  public executionStatusChanged: Subject<void> = new Subject<void>();
  // use subject to inform about updated execution status
  public executionStatus: IParkerExecutionStatus[] = [];
  public progressInfo: ProgressInfo;

  private readonly _pageSize = 5; // a browser can handle 9 concurrent requests to the server efficiently at max
  private endSubject: Subject<number> = new Subject<number>();

  // load any data needed for the execution
  protected abstract prepare(): Observable<void>;
  protected abstract preFilterParkersAndUpdateExecutionStatus(parkers: ParkerDTO[]): ParkerDTO[];
  protected abstract executeFunctionOnParker(parker: ParkerDTO): Observable<number>;

  constructor(numTotalRecordsToWorkOn: number) {
    this.progressInfo = new ProgressInfo(numTotalRecordsToWorkOn);
  }

  public cancelExecution() {
    this.endSubject?.next(1);
    this.endSubject?.complete();
  }

  public run(loadParkersPagedCallback: (skip: number, take: number) => Observable<PagedResultDTOOfParkerDTO>): void {
    this.progressInfo.reset();
    this.executionStatus = [];
    this.prepare().subscribe({next: () => {
      this.endSubject = new Subject<number>();
      const subject = new Subject<number>();
      subject.pipe(
        takeUntil(this.endSubject),
        switchMap(skip => {
          return loadParkersPagedCallback(skip, this._pageSize).pipe(map(page => {
            return {skip, page};
          }));
        }),
        switchMap(res => {
          var parkersToExecute: ParkerDTO[] = [];
          parkersToExecute = this.preFilterParkersAndUpdateExecutionStatus(res.page.items);
          if (parkersToExecute.length != res.page.items.length) {
            // increment iterations by the number of parkers that are excluded from execution
            this.progressInfo.incrementIterations(res.page.items.length - parkersToExecute.length);
            this.executionStatusChanged.next();
          }

          if (parkersToExecute.length > 0) {
            return forkJoin([...parkersToExecute.map(parker => this.executeFunctionOnParker(parker).pipe(
              tap(() => {
                this.handleSuccess(new ParkerInfo(parker));
              }),
              map(() => parker.id),
              catchError(error => {
                return this.handleError(new ParkerInfo(parker), error);
              })),
            )]).pipe(map(() => res));
          }
          else {
            return of(res);
          }
        })
      ).subscribe({ next: (res) => {
        const newSkip = res.skip + this._pageSize;
        if (newSkip < res.page.totalNumberOfItems) {
          subject.next(newSkip);
        } else {
          this.endSubject.next(1);
          this.endSubject.complete();
          subject.complete();
          this.functionExecuted.next();
        }
      }, error: (error) => {
        this.endSubject.next(1);
        this.endSubject.complete();
        subject.complete();
        this.executionStatus.push({
          status: 'error',
          message: error.message,
          parkerInfo: new ParkerInfo({id: 0, firstName: 'error', lastName: 'function execution'} as ParkerDTO)
        });
        this.executionStatusChanged.next();
      }});
      subject.next(0);
    }, error: (error) => {
      this.executionStatus.push({
        status: 'error',
        message: error.message,
        parkerInfo: new ParkerInfo({id: 0, firstName: 'function', lastName: 'preparation failed'} as ParkerDTO)
      });
      this.executionStatusChanged.next();
    }
    });
  }

  private handleError(parkerInfo: ParkerInfo, error: any) {
    this.progressInfo.incrementIteration();
    this.executionStatus.push({
      status: 'error',
      message: error.message,
      parkerInfo
    });
    this.executionStatusChanged.next();
    return of(parkerInfo.id);
  }

  private handleSuccess(parkerInfo: ParkerInfo) {
    this.progressInfo.incrementIteration();
    // we do not add success messages to the execution status to avoid overfilling -> we are only interested in errors -> nothing to do here
    this.executionStatusChanged.next();
  }
}
